/**
 * All course CRUD actions
 *
 * Actions are payloads of information that send data from the application
 * (i.e. Yote server) to the store. They are the _only_ source of information
 * for the store.
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import api utility
import callAPI from '../../global/utils/api'

const shouldFetchSingle = (state, id) => {
  /**
   * This is helper method to determine whether we should fetch a new single
   * user object from the server, or if a valid one already exists in the store
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetch single");
  const { byId, selected } = state.course;
  if(selected.id !== id) {
    // the "selected" id changed, so we _should_ fetch
    // console.log("Y shouldFetch - true: id changed");
    return true;
  } else if(selected.isFetching) {
    // "selected" is already fetching, don't do anything
    // console.log("Y shouldFetch - false: isFetching");
    return false;
  } else if(!byId[id] && !selected.error) {
    // the id is not in the map, fetch from server
    // however, if the api returned an error, then it SHOULDN'T be in the map
    // so re-fetching it will result in an infinite loop
    // console.log("Y shouldFetch - true: not in map");
    return true;
  } else if(new Date().getTime() - selected.lastUpdated > (1000 * 60 * 5)) {
    // it's been longer than 5 minutes since the last fetch, get a new one
    // console.log("Y shouldFetch - true: older than 5 minutes");
    // also, don't automatically invalidate on server error. if server throws an error,
    // that won't change on subsequent requests and we will have an infinite loop
    return true;
  } else {
    // if "selected" is invalidated, fetch a new one, otherwise don't
    // console.log("Y shouldFetch - " + selected.didInvalidate + ": didInvalidate");
    return selected.didInvalidate;
  }
}

export const INVALIDATE_SELECTED_COURSE = "INVALIDATE_SELECTED_COURSE"
export function invalidateSelected() {
  return {
    type: INVALIDATE_SELECTED_COURSE
  }
}

export const fetchSingleIfNeeded = (id) => (dispatch, getState) => {
  if (shouldFetchSingle(getState(), id)) {
    return dispatch(fetchSingleCourseById(id))
  } else {
    return dispatch(returnSingleCoursePromise(id)); // return promise that contains course
  }
}

export const returnSingleCoursePromise = (id) => (dispatch, getState) => {
  /**
   * This returns the object from the map so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. this is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */
  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_SINGLE_COURSE_WITHOUT_FETCHING"
      , id: id
      , item: getState().course.byId[id]
      , success: true
    })
  });
}

export const REQUEST_SINGLE_COURSE = "REQUEST_SINGLE_COURSE";
function requestSingleCourse(id) {
  return {
    type: REQUEST_SINGLE_COURSE
    , id
  }
}

export const RECEIVE_SINGLE_COURSE = "RECEIVE_SINGLE_COURSE";
function receiveSingleCourse(json) {
  return {
    type: RECEIVE_SINGLE_COURSE
    , id: json.course ? json.course._id : null
    , item: json.course
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function fetchSingleCourseById(courseId) {
  return dispatch => {
    dispatch(requestSingleCourse(courseId))
    return callAPI(`/api/courses/${courseId}`)
      .then(json => dispatch(receiveSingleCourse(json)))
  }
}

export const ADD_SINGLE_COURSE_TO_MAP = "ADD_SINGLE_COURSE_TO_MAP";
export function addSingleCourseToMap(item) {
  return {
    type: ADD_SINGLE_COURSE_TO_MAP
    , item
  }
}


export const REQUEST_SINGLE_COURSE_BY_SLUG = "REQUEST_SINGLE_COURSE_BY_SLUG";
function requestSingleCourseBySlug(slug) {
  return {
    type: REQUEST_SINGLE_COURSE_BY_SLUG
    , slug
  }
}

export function fetchSingleBySlug(courseSlug) {
  return dispatch => {
    dispatch(requestSingleCourseBySlug(courseSlug))
    return callAPI(`/api/courses/by-slug/${courseSlug}`)
      .then(json => dispatch(receiveSingleCourse(json)))
  }
}

export const REQUEST_CREATE_COURSE = "REQUEST_CREATE_COURSE";
function requestCreateCourse(course) {
  return {
    type: REQUEST_CREATE_COURSE
    , course
  }
}

export const RECEIVE_CREATE_COURSE = "RECEIVE_CREATE_COURSE";
function receiveCreateCourse(json) {
  return {
    type: RECEIVE_CREATE_COURSE
    , id: json.course ? json.course._id : null
    , item: json.course
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendCreateCourse(data) {
  return dispatch => {
    dispatch(requestCreateCourse(data))
    return callAPI('/api/courses', 'POST', data)
      .then(json => dispatch(receiveCreateCourse(json)))
  }
}

export const REQUEST_UPDATE_COURSE = "REQUEST_UPDATE_COURSE";
function requestUpdateCourse(course) {
  return {
    type: REQUEST_UPDATE_COURSE
    , id: course._id
    , course
  }
}

export const RECEIVE_UPDATE_COURSE = "RECEIVE_UPDATE_COURSE";
function receiveUpdateCourse(json) {
  return {
    type: RECEIVE_UPDATE_COURSE
    , id: json.course ? json.course._id : null
    , item: json.course
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendUpdateCourse(data) {
  return dispatch => {
    dispatch(requestUpdateCourse(data))
    return callAPI(`/api/courses/${data._id}`, 'PUT', data)
      .then(json => dispatch(receiveUpdateCourse(json)))
  }
}

export const REQUEST_DELETE_COURSE = "REQUEST_DELETE_COURSE";
function requestDeleteCourse(courseId) {
  return {
    type: REQUEST_DELETE_COURSE
    , courseId
  }
}

export const RECEIVE_DELETE_COURSE = "RECEIVE_DELETE_COURSE";
function receiveDeleteCourse(json) {
  return {
    type: RECEIVE_DELETE_COURSE
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendDelete(id) {
  return dispatch => {
    dispatch(requestDeleteCourse(id))
    return callAPI(`/api/courses/${id}`, 'DELETE')
      .then(json => dispatch(receiveDeleteCourse(json)))
  }
}


/**
 * COURSE LIST ACTIONS
 */

const findListFromArgs = (state, listArgs) => {
  /**
   * Helper method to find appropriate list from listArgs.
   *
   * Because we nest courseLists to arbitrary locations/depths,
   * finding the correct list becomes a little bit harder
   */
  // let list = Object.assign({}, state.course.lists, {});
  let list = { ...state.course.lists }
  for(let i = 0; i < listArgs.length; i++) {
    list = list[listArgs[i]];
    if(!list) {
      return false;
    }
  }
  return list;
}

const shouldFetchList = (state, listArgs) => {
  /**
   * Helper method to determine whether to fetch the list or not from arbitrary
   * listArgs
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetchList with these args ", listArgs, "?");
  const list = findListFromArgs(state, listArgs);
  // console.log("LIST in question: ", list);
  if(!list || !list.items) {
    // yes, the list we're looking for wasn't found
    // console.log("X shouldFetch - true: list not found");
    return true;
  } else if(list.isFetching) {
    // no, this list is already fetching
    // console.log("X shouldFetch - false: fetching");
    return false
  } else if(new Date().getTime() - list.lastUpdated > (1000 * 60 * 5)) {
    // yes, it's been longer than 5 minutes since the last fetch
    // console.log("X shouldFetch - true: older than 5 minutes");
    return true;
  } else {
    // maybe, depends on if the list was invalidated
    // console.log("X shouldFetch - " + list.didInvalidate + ": didInvalidate");
    return list.didInvalidate;
  }
}

export const fetchListIfNeeded = (...listArgs) => (dispatch, getState) => {
  if(listArgs.length === 0) {
    // If no arguments passed, make the list we want "all"
    listArgs = ["all"];
  }
  if(shouldFetchList(getState(), listArgs)) {
    return dispatch(fetchList(...listArgs));
  } else {
    return dispatch(returnCourseListPromise(...listArgs));
  }
}

export const returnCourseListPromise = (...listArgs) => (dispatch, getState) => {
  /**
   * This returns the list object from the reducer so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. This is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */

  // return the array of objects just like the regular fetch
  const state = getState();
  const listItemIds = findListFromArgs(state, listArgs).items
  const listItems = listItemIds.map(id => state.course.byId[id]);

  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_COURSE_LIST_WITHOUT_FETCHING"
      , listArgs: listArgs
      , list: listItems
      , success: true
    })
  });
}

export const REQUEST_COURSE_LIST = "REQUEST_COURSE_LIST"
function requestCourseList(listArgs) {
  return {
    type: REQUEST_COURSE_LIST
    , listArgs
  }
}

export const RECEIVE_COURSE_LIST = "RECEIVE_COURSE_LIST"
function receiveCourseList(json, listArgs) {
  return {
    type: RECEIVE_COURSE_LIST
    , listArgs
    , list: json.courses
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export const ADD_COURSE_TO_LIST = "ADD_COURSE_TO_LIST";
export function addCourseToList(id, ...listArgs) {
  // console.log("Add course to list", id);
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: ADD_COURSE_TO_LIST
    , id
    , listArgs
  }
}

export const REMOVE_COURSE_FROM_LIST = "REMOVE_COURSE_FROM_LIST"
export function removeCourseFromList(id, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ['all'];
  }
  return {
    type: REMOVE_COURSE_FROM_LIST
    , id
    , listArgs
  }
}

export function fetchList(...listArgs) {
  return dispatch => {
    if(listArgs.length === 0) {
      // default to "all" list if we don't pass any listArgs
      listArgs = ["all"];
    }
    dispatch(requestCourseList(listArgs))
    /**
     * determine what api route we want to hit
     *
     * NOTE: use listArgs to determine what api call to make.
     * if listArgs[0] == null or "all", return list
     *
     * if listArgs has 1 arg, return "/api/courses/by-[ARG]"
     *
     * if 2 args, additional checks required.
     *  if 2nd arg is a string, return "/api/courses/by-[ARG1]/[ARG2]".
     *    ex: /api/courses/by-category/:category
     *  if 2nd arg is an array, though, return "/api/courses/by-[ARG1]-list" with additional query string
     *
     * TODO:  make this accept arbitrary number of args. Right now if more
     * than 2, it requires custom checks on server
     */
    let apiTarget = "/api/courses";
    if(listArgs.length == 1 && listArgs[0] !== "all") {
      apiTarget += `/by-${listArgs[0]}`;
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1])) {
      // length == 2 has it's own check, specifically if the second param is an array
      // if so, then we need to call the "listByValues" api method instead of the regular "listByRef" call
      // this can be used for querying for a list of courses given an array of course id's, among other things
      apiTarget += `/by-${listArgs[0]}-list?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `${listArgs[0]}=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2) {
      // ex: ("author","12345")
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
    } else if(listArgs.length > 2) {
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
      for(let i = 2; i < listArgs.length; i++) {
        apiTarget += `/${listArgs[i]}`;
      }
    }
    return callAPI(apiTarget).then(
      json => dispatch(receiveCourseList(json, listArgs))
    )
  }
}

/**
 * LIST UTIL METHODS
 */
export const SET_COURSE_FILTER = "SET_COURSE_FILTER"
export function setFilter(filter, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_COURSE_FILTER
    , filter
    , listArgs
  }
}

export const SET_COURSE_PAGINATION = "SET_COURSE_PAGINATION"
export function setPagination(pagination, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_COURSE_PAGINATION
    , pagination
    , listArgs
  }
}

export const INVALIDATE_COURSE_LIST = "INVALIDATE_COURSE_LIST"
export function invalidateList(...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: INVALIDATE_COURSE_LIST
    , listArgs
  }
}



// WEBPACK FOOTER //
// modules/course/courseActions.js